<template>
  <form-item
    v-if="isForm"
    :isBindAttrs="true"
    v-bind="$attrs"
    :name="name"
    :prop="prop"
    :rules="rules"
    :label="label"
    :descContent="descContent"
    >
    <el-tooltip
      class="item"
      effect="dark"
      placement="left"
      :disabled="unhideIDField === '' && !hasLeftTooltip"
      :tabindex="-1"
      >
      <div slot="content" class="tool-tip-content">
        {{ leftTooltipText }}
      </div>
      <div>
        <el-input
          v-model="val"
          @input="onInput"
          class="form-item"
          :prefix-icon="prefixIcon"
          :maxlength="$attrs.maxlength"
          :type="$attrs.type"
          :min="$attrs.min"
          :autosize="autosize"
          :placeholder="placeholder"
          :disabled="disabled"
          @focus="hasTransFocus = false"
          @change="handleChange"
          @blur="handleBlur"
          :clearable="clearable"
          >
          <template slot="prepend">
            <slot name="prepend-input"></slot>
          </template>
          <template slot="append">
            <slot name="append-input"></slot>
            <el-checkbox
              v-if="hasTrans && showTrans"
              v-model="checkTransAll"
              :indeterminate="isTransIndeterminate"
              @change="handleCheckTransAllChange"
              >
            </el-checkbox>
            <div v-if="isTransField">
              <div v-if="subName === ''">
                <el-button
                  v-if="transField.isDef"
                  icon="el-icon-copy-document"
                  @click="transField.copy(name)"
                  >
                  <i class="el-icon-d-arrow-right"></i>
                </el-button>
                <el-checkbox
                  v-else
                  v-model="transField.data[getStatusField(name)]"
                  :true-label="1"
                  :false-label="0"
                  @change="transField.handleCheckChange"
                  >
                </el-checkbox>
              </div>
              <div v-else>
                <el-button
                  v-if="transField.isDef"
                  icon="el-icon-copy-document"
                  @click="transField.copy(subFieldName, subName, subIdx)"
                  >
                  <i class="el-icon-d-arrow-right"></i>
                </el-button>
                <el-checkbox
                  v-else-if="transField.data[subName]"
                  v-model="transField.data[subName][subIdx][getStatusField(subFieldName)]"
                  :true-label="1"
                  :false-label="0"
                  @change="transField.handleCheckChange"
                  >
                </el-checkbox>
              </div>
            </div>
          </template>
        </el-input>
        <text-length
          v-if="$attrs.charLen && val"
          :text="val"
          :maxlength="$attrs.charLen"
          >
        </text-length>
      </div>
    </el-tooltip>
    <div v-if="isTransField && $attrs.type === 'textarea'">
      <el-button
        v-if="transField.isDef"
        class="trans-action"
        icon="el-icon-copy-document"
        @click="transField.copy(name)"
        >
        {{ $t('api.trans.copyText') }}
        <i class="el-icon-d-arrow-right"></i>
      </el-button>
      <el-checkbox
        v-else
        v-model="transField.data[getStatusField(name)]"
        :true-label="1"
        :false-label="0"
        :disabled="disabled"
        @change="transField.handleCheckChange"
        >
        {{ $t('api.trans.checked') }}
      </el-checkbox>
    </div>
    <!-- 格式檢查按鈕 & 翻譯按鈕 -->
    <div v-if="showGrammarChecker || hasTrans || hasToolbarSlot" class="toolbar">
      <slot name="prepend-toolbar"></slot>
      <el-tooltip v-if="showGrammarChecker" placement="top-start" :content="grammarConfig.tooltipLabel" :disabled="!grammarConfig.tooltipLabel || !isGrammarCheckerInvalid">
        <el-button
          class="check-grammar-btn"
          type="primary"
          :loading="isLoadingGrammar"
          :disabled="isGrammarCheckerInvalid"
          @click="onGrammarBtnClick"
        >
          <span>{{ grammarConfig.buttonLabel || $t('button_format_check') }}</span>
        </el-button>
      </el-tooltip>
      <!-- 翻譯按鈕 -->
      <el-badge v-if="hasTrans" class="item trans-checked" :is-dot="isTransNeedReview">
        <el-button class="trans" :type="showTrans ? 'primary' : 'default'" @click="toggleTrans">
          <svg-icon icon-class="language"></svg-icon>
          {{ $t('api.translation') }}
          <i :class="showTrans ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </el-button>
      </el-badge>

      <slot name="append-toolbar"></slot>

    </div>
    <div v-if="showGrammarChecker && (isLoadingGrammar || !!grammarResult || isGrammarError)" class="ai-grammar-checker-result">
      <div class="grammar-checker-result__content">
        <template v-if="grammarResult">{{ grammarResult }}</template>
        <template v-if="isLoadingGrammar">{{ $t('textarea_generating_data_hint')}}</template>
        <template v-if="isGrammarError">{{ $t('alert_data_unavailable')}}</template>
      </div>
      <template v-if="!!grammarResult">
        <el-alert show-icon type="warning" :closable="false" :title="$t('alert_apply_will_replace_content_hint')" />
        <el-button class="apply-grammar-btn" type="primary" @click="onApplyGrammarBtnClick">
          <span>{{ $t('button_apply_directly') }}</span>
        </el-button>
      </template>
    </div>
    <!-- 展開翻譯區塊 -->
    <div
      v-if="hasTrans"
      class="translation-section"
      >
      <template v-if="showTrans">
        <div v-if="showAutoTranslate" class="translation-section__header">
          <el-alert show-icon type="warning" :closable="false" :title="$t('alert_auto_translate_overwrite_warning')" />
          <el-button
            type="primary"
            :loading="isLoadingTranslation"
            :disabled="!val"
            @click="onGenerateTranslation"
          >
            <span>{{ $t('button_auto_translate') }}</span>
          </el-button>
          <el-alert v-if="isTranslationError" show-icon type="error" :closable="false" :title="$t('alert_data_unavailable')" />
        </div>
        <el-card
          class="trans"
          shadow="hover"
          >
          <el-row
            v-if="showTrans && $attrs.type === 'textarea'"
            type="flex"
            justify="space-between"
            >
            <el-col :span="6">
              {{ $t('api.trans.all') }}
            </el-col>
            <el-col :span="6"></el-col>
            <el-col :span="6" class="right">
              <el-checkbox
                v-model="checkTransAll"
                :indeterminate="isTransIndeterminate"
                @change="handleCheckTransAllChange"
                >
                {{ $t('api.trans.checked') }}
              </el-checkbox>
            </el-col>
          </el-row>
        </el-card>
      </template>
      <el-card
        v-if="showTrans"
        v-for="(lang, idx) in transData"
        :key="idx"
        class="trans"
        shadow="hover"
        >
        <el-row
          v-if="$attrs.type === 'textarea'"
          type="flex"
          justify="space-between"
          >
          <el-col :span="6">
            <el-tag
              effect="plain"
              type="info"
              >
              <flag-item
                :id="lang.lang_id"
                :code="lang.code"
                >
              </flag-item>
            </el-tag>
          </el-col>
          <el-col :span="6"></el-col>
          <el-col :span="6" class="right">
            <el-checkbox
              v-model="lang[getStatusField(name)]"
              :true-label="1"
              :false-label="0"
              @change="handleCheckTransChange"
              >
              {{ $t('api.trans.checked') }}
            </el-checkbox>
          </el-col>
        </el-row>
        <!-- FIXME: 直接修改 props 欄位違反 vue 的資料流，修改時候記得避免不斷 emit transData 造成換面卡頓 -->
        <el-input
          class="form-item"
          v-model="lang[name]"
          :maxlength="$attrs.maxlength"
          :type="$attrs.type"
          :autosize="autosize"
          :placeholder="placeholder"
          :disabled="disabled || isLoadingTranslation"
          :clearable="clearable"
          @input="onInput"
          @change="onTransChange"
          @focus="setTransFocus(idx, true)"
          >
          <template slot="prepend">
            <flag-item
              :id="lang.lang_id"
              :code="lang.code"
              >
            </flag-item>
            <slot name="prepend-input"></slot>
          </template>
          <template slot="append">
            <slot name="append-input"></slot>
            <el-checkbox
              v-model="lang[getStatusField(name)]"
              :true-label="1"
              :false-label="0"
              @change="handleCheckTransChange"
              >
            </el-checkbox>
          </template>
        </el-input>
        <el-row
          v-show="hasTransFocus && idx === transFocusIdx"
          class="trans-action"
          >
          <el-col>
            <el-button
              size="small"
              icon="el-icon-copy-document"
              @click="copyText(lang)"
              >
              {{ $t('api.trans.copyText') }}
            </el-button>
            <convert-chinese
              v-if="lang.code.startsWith('zh')"
              size="small"
              :text="val"
              :lang="lang"
              :transData="transData"
              :name="name"
              >
            </convert-chinese>
          </el-col>
        </el-row>
      </el-card>
    </div>
    <slot name="append"></slot>
  </form-item>
  <el-table-column
    v-else
    v-bind="$attrs"
    :label="label"
    :header-align="$attrs['header-align'] || $attrs.align || 'center'"
    :align="$attrs.align || 'center'"
    >
    <template slot-scope="scope">
      <el-tooltip
        placement="top"
        v-if="hasTooltip && (scope.row.contact_phone || scope.row.contact_mobile)"
        effect="light"
        >
        <div slot="content">
          <slot tooltipContent
            :phone="scope.row.contact_phone"
            :mobile="scope.row.contact_mobile"
            >
          </slot>
        </div>
        <span
          v-text="scope.row[name]"
          >
        </span>
      </el-tooltip>
      <el-link
        v-else-if="isURL"
        :href="scope.row[name]"
        target="_blank"
        >
        {{ scope.row[name] }}
      </el-link>
      <span
        v-else
        v-text="isNumber ? addCommas(scope.row[dataName]) : scope.row[dataName]"
      >
      </span>
    </template>
  </el-table-column>
</template>

<script>
import { ref, computed } from 'vue'
import { addCommas, parseFloatPrecision } from '@/utils/number'
import { getTransText, getLabelText, getStatusField } from '@/utils/i18n'

import { AItranslationTemplate } from '@/enums/aiRelativeTools'

import { useGrammarChecker } from '@/composables/useGrammarChecker'
import { useTranslationGenerator } from '@/composables/useTranslationGenerator'

const regIsNum = /^-?(\d+\.?\d*)$|(\d*\.?\d+)$/

const defaultGrammarConfig = {
  content: '',
  currencyCode: '',
  template: '',
  tooltipLabel: '',
  buttonLabel: ''
}

export default {
  components: {
    TextLength: () => import('@/components/TextLength'),
    ConvertChinese: () => import('@/components/ConvertChinese')
  },
  name: 'text-field',
  props: {
    value: null,
    disabled: Boolean,
    hasPlaceHolder: Boolean,
    name: {
      type: String,
      required: true
    },
    base: {
      type: String,
      default: ''
    },
    data: {
      type: String,
      default: ''
    },
    unhideID: {
      type: Number,
      default: 0
    },
    unhideIDField: {
      type: String,
      default: ''
    },
    labelText: {
      type: String,
      default: null
    },
    placeholderText: {
      type: String,
      default: null
    },
    descText: {
      type: String,
      default: null
    },
    isLengthDesc: {
      type: Boolean,
      default: false
    },
    showPresetDesc: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasTooltip: {
      type: Boolean,
      default: false
    },
    hasLeftTooltip: {
      type: Boolean,
      default: false
    },
    leftTooltipContent: {
      type: String,
      default: ''
    },
    hasTrans: {
      type: Boolean,
      default: false
    },
    isTransField: {
      type: Boolean,
      default: false
    },
    transField: {
      type: Object,
      default: () => {
        return null
      }
    },
    subName: {
      type: String,
      default: ''
    },
    subFieldName: {
      type: String
    },
    subIdx: {
      type: Number
    },
    isURL: {
      type: Boolean,
      default: false
    },
    isNumber: {
      type: Boolean,
      default: false
    },
    isUppercase: {
      type: Boolean,
      default: false
    },
    prop: {
      type: String
    },
    oriData: null,
    transData: {
      type: Array,
      default: () => []
    },
    autosize: {
      type: Object,
      default: () => {
        return {
          minRows: 5,
          maxRows: 10
        }
      }
    },
    dataType: {
      type: String,
      default: 'text'
    },
    isForm: {
      type: Boolean,
      default: true
    },
    isNullable: {
      type: Boolean,
      default: false
    },
    isAllowZero: {
      type: Boolean,
      default: false
    },
    prefixIcon: {
      type: String
    },
    precision: {
      type: Number,
      default: null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    min: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    grammarConfig: {
      type: Object
    },
    showAutoTranslate: {
      type: Boolean,
      default: false
    },
    autoTranslateTemplate: {
      type: String,
      default: AItranslationTemplate.Default
    }
  },
  emits: ['input', 'change'],
  setup (props, { emit, slots }) {
    const val = ref(props.value)
    const dataName = ref(props.data || props.name)
    const showTrans = ref(false)
    const checkTransAll = ref(false)
    const isTransIndeterminate = ref(false)
    const transFocusIdx = ref(0)
    const hasTransFocus = ref(false)

    const {
      value: grammarResult,
      isError: isGrammarError,
      loading: isLoadingGrammar,
      isInvalid: isGrammarCheckerInvalid,
      resetGrammarChecker,
      fetchGrammarChecker
    } = useGrammarChecker(computed(() => props.grammarConfig || defaultGrammarConfig))
    const {
      value: translationMap,
      loading: isLoadingTranslation,
      isError: isTranslationError,
      fetchTranslation,
      getTranslationByLangCode
    } = useTranslationGenerator(computed(() => ({ content: val.value, template: props.autoTranslateTemplate })))

    const showGrammarChecker = computed(() => !!props.grammarConfig)
    const hasToolbarSlot = computed(() => !!slots['prepend-toolbar'] || !!slots['append-toolbar'])
    // FIXME: 直接修改 props 欄位違反 vue 的資料流，修改時候記得避免不斷 emit transData 造成換面卡頓
    async function onGenerateTranslation () {
      await fetchTranslation()

      if (!Object.values(translationMap.value).length) {
        return
      }

      const updatedField = props.name
      props.transData.forEach(lang => {
        const result = getTranslationByLangCode(lang.code)
        if (result) {
          lang[updatedField] = result
          lang[getStatusField(updatedField)] = 1
        }
      })
    }

    function onGrammarBtnClick () {
      fetchGrammarChecker()
    }

    function onApplyGrammarBtnClick () {
      val.value = grammarResult.value
      onInput()
      resetGrammarChecker()
    }

    function onInput () {
      emit('input', val.value)
    }

    return {
      val,
      dataName,
      showTrans,
      checkTransAll,
      isTransIndeterminate,
      isLoadingTranslation,
      isGrammarError,
      isTranslationError,
      hasToolbarSlot,
      transFocusIdx,
      hasTransFocus,
      showGrammarChecker,
      grammarResult,
      isLoadingGrammar,
      isGrammarCheckerInvalid,
      fetchGrammarChecker,
      onGenerateTranslation,
      onInput,
      onApplyGrammarBtnClick,
      onGrammarBtnClick
    }
  },
  computed: {
    rules () {
      return this.getRules(this.required)
    },
    isTransNeedReview () {
      let res = false
      for (let idx in this.transData) {
        let s = this.transData[idx][this.getStatusField(this.name)]
        if (s === undefined || s === 0) {
          res = true
        }
      }
      return res
    },
    label () {
      if (this.labelText !== null) {
        return this.labelText
      }
      var base = 'api.' + this.base + '.fields.' + this.name
      if (!this.isForm && this.$te(base + '.listLabel')) {
        return this.$t(base + '.listLabel')
      }
      return this.getTransText(this.name, 'label', this.base)
    },
    placeholder () {
      if (this.placeholderText !== null) {
        return this.placeholderText
      }
      if (this.disabled && !this.hasPlaceHolder) {
        return ''
      }
      let p = this.getTransText(this.name, 'placeholder', this.base)
      if (this.isLengthDesc) {
        return p + ' ' + this.textLengthDesc
      }
      return p
    },
    textLengthDesc () {
      if (this.descText !== null) {
        return this.descText
      }
      return this.$t('api.default.textLength.desc', {
        fullWidthLen: this.$attrs.fullWidthLen,
        charLen: this.$attrs.charLen
      })
    },
    descContent () {
      if (this.descText !== null) {
        return this.descText
      }
      if (this.isLengthDesc) {
        if (this.showPresetDesc) {
          return this.getTransText(this.name, 'desc', this.base) + ' (' + this.textLengthDesc + ')'
        }
        return this.textLengthDesc
      }
      return this.getTransText(this.name, 'desc', this.base)
    },
    leftTooltipText () {
      if (this.leftTooltipContent !== '') {
        return this.leftTooltipContent
      }

      return this.getLabelText(this.base, this.unhideIDField) + ':' + this.unhideID
    }
  },
  methods: {
    addCommas,
    parseFloatPrecision,
    getTransText,
    getLabelText,
    getStatusField,

    handleChange (v) {
      this.$emit('change', v)
    },
    onTransChange () {
      this.setTransStatus()
    },
    checkEmptyValue (v) {
      return (v === '' || v === null || v === undefined)
    },
    setTransStatus (isUpdateVal) {
      if (this.hasTrans) {
        let sf = this.getStatusField(this.name)
        if (this.checkEmptyValue(this.val)) {
          for (let idx in this.transData) {
            let item = this.transData[idx]
            if (this.checkEmptyValue(item[this.name])) {
              this.$set(item, sf, 0)
            }
          }
          this.handleCheckTransChange()
        } else if (this.val !== this.oriData[this.name] && isUpdateVal) {
          for (let idx in this.transData) {
            let item = this.transData[idx]
            this.$set(item, sf, 0)
          }
          this.handleCheckTransChange()
        }
      }
    },
    setTransFocus (idx, val) {
      this.transFocusIdx = idx
      this.hasTransFocus = val
    },
    copyText (lang) {
      this.$set(lang, this.name, this.val)
      this.$set(lang, this.getStatusField(this.name), 1)
    },
    toggleTrans () {
      this.showTrans = !this.showTrans
      if (this.showTrans) {
        this.handleCheckTransChange()
      }
    },
    handleCheckTransAllChange (val) {
      let v = val ? 1 : 0
      let n = this.getStatusField(this.name)
      for (let idx in this.transData) {
        this.$set(this.transData[idx], n, v)
      }
      this.isTransIndeterminate = false
    },
    handleCheckTransChange () {
      let uncheckedCount = 0
      let n = this.getStatusField(this.name)
      for (let idx in this.transData) {
        let s = this.transData[idx][n]
        if (s === 0 || s === undefined) {
          uncheckedCount++
        }
      }
      this.checkTransAll = (uncheckedCount === 0)
      this.isTransIndeterminate = uncheckedCount > 0 && uncheckedCount < this.transData.length
    },
    handleBlur (event) {
      switch (this.dataType) {
        case 'price':
          if (this.precision !== null) {
            this.val = this.parseFloatPrecision(event.target.value, this.precision)
            this.$emit('input', this.val)
          }
          break
      }
    },
    isEmpty (v) {
      return (v === null || v === '' || v === undefined)
    },
    checkPrice (rule, value, callback) {
      if (this.isEmpty(value)) {
        if (rule.required) {
          return callback(new Error(
            this.getTransText(this.name, 'error', this.base)
          ))
        } else {
          return callback()
        }
      }

      if (value === 0 && !this.isAllowZero && rule.required) {
        return callback(new Error(
          this.getTransText(this.name, 'error', this.base)
        ))
      }

      setTimeout(() => {
        if (!regIsNum.test(value)) {
          callback(new Error(
            this.getTransText(this.name, 'errNum', this.base)
          ))
        } else {
          const isInvalid = (this.isAllowZero && value < 0) || (!this.isAllowZero && rule.required && value <= 0)
          const messageKey = this.isAllowZero ? 'errLessThanZero' : 'errLessThanOrEqualZero'

          if (isInvalid) {
            callback(new Error(this.getTransText(this.name, messageKey, this.base)))
          } else {
            callback()
          }
        }
      }, 50)
    },
    checkRefund (rule, value, callback) {
      if (this.isEmpty(value)) {
        if (rule.required) {
          return callback(new Error(
            this.getTransText(this.name, 'error', this.base)
          ))
        } else {
          return callback()
        }
      }
      setTimeout(() => {
        if (!regIsNum.test(value)) {
          callback(new Error(
            this.getTransText(this.name, 'errNum', this.base)
          ))
        } else {
          callback()
        }
      }, 50)
    },
    checkRequired (rule, value) {
      if (this.isEmpty(value)) {
        if (rule.required) {
          return new Error(
            this.getTransText(this.name, 'error', this.base)
          )
        }
        return false
      }
    },
    checkNum (value, min, max) {
      if (!Number.isInteger(value)) {
        return new Error(
          this.getTransText(this.name, 'errNum', this.base)
        )
      }
      if (value < min || value > max) {
        return new Error(
          this.getTransText(this.name, 'errNumRange', this.base) +
          ` (${min} ~ ${max})`
        )
      }
    },
    checkNetDay (rule, value, callback) {
      this.checkUINTBase(rule, value, callback, 1, 99)
    },
    checkUINT (rule, value, callback) {
      this.checkUINTBase(rule, value, callback, 0, 4294967295)
    },
    checkUINTBase (rule, value, callback, min, max) {
      const err = this.checkRequired(rule, value, callback)
      if (err !== undefined) {
        if (err !== false) {
          return callback(err)
        }
        return callback()
      }
      if (this.min !== null) {
        min = this.min
      }
      if (this.max !== null) {
        max = this.max
      }
      const errNum = this.checkNum(value, min, max)
      if (errNum !== undefined) {
        return callback(errNum)
      }

      callback()
    },
    checkUINT8 (rule, value, callback) {
      const err = this.checkRequired(rule, value, callback)
      if (err !== undefined) {
        if (err !== false) {
          return callback(err)
        }
        return callback()
      }
      const min = 0
      const max = 255
      const errNum = this.checkNum(value, min, max)
      if (errNum !== undefined) {
        return callback(errNum)
      }

      callback()
    },
    checkPrecision (rule, value, callback) {
      if (this.isEmpty(value)) {
        if (rule.required) {
          return callback(new Error(
            this.getTransText(this.name, 'error', this.base)
          ))
        } else {
          return callback()
        }
      }
      setTimeout(() => {
        if (!Number.isInteger(value)) {
          callback(new Error(
            this.getTransText(this.name, 'errNum', this.base)
          ))
        } else {
          if (value > 4 || value < 0) {
            callback(new Error(
              this.getTransText(this.name, 'errPrecision', this.base)
            ))
          } else {
            callback()
          }
        }
      }, 50)
    },
    getRules (v) {
      let def = [{
        required: v,
        message: this.getTransText(this.name, 'error', this.base),
        trigger: 'blur'
      }]
      switch (this.dataType) {
        case 'precision':
          def = [{
            required: v,
            validator: this.checkPrecision,
            trigger: 'blur'
          }]
          break
        case 'uint8':
          def = [{
            required: v,
            validator: this.checkUINT8,
            trigger: 'blur'
          }]
          break
        case 'netDay':
          def = [{
            required: v,
            validator: this.checkNetDay,
            trigger: 'blur'
          }]
          break
        case 'uint':
          def = [{
            required: v,
            validator: this.checkUINT,
            trigger: 'blur'
          }]
          break
        case 'refund':
          def = [{
            required: v,
            validator: this.checkRefund,
            trigger: 'blur'
          }]
          break
        case 'price':
          def = [{
            required: v,
            validator: this.checkPrice,
            trigger: 'blur'
          }]
          break
        case 'email':
          def = [{
            required: v,
            type: 'email',
            message: this.$t('api.user.error.auth_id_email'),
            trigger: 'blur'
          }]
          break
      }
      let rules = this.$attrs.rules
      if (rules) {
        return def.concat(rules)
      }
      return def
    }
  },
  watch: {
    value (newValue) {
      this.val = (this.isUppercase) ? newValue.toLocaleUpperCase() : newValue
      this.setTransStatus(true)
      switch (this.dataType) {
        case 'price':
          // reset to null prevent API decimal.NullDecimal parsing error for empty string
          if (newValue === '') {
            this.val = null
            this.$emit('input', this.val)
          }
          break
      }
    },
    'transData': {
      handler: function () {
        this.setTransStatus()
      },
      deep: true,
      immediate: true
    }
  }
}
</script>
<style lang="scss" scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  margin: 10px 0;
  button {
    margin: 0;
  }
  .trans-checked {
    margin-left: auto;
  }
}
.grammar-checker-result__content {
  margin: 10px 0;
  background: #ecf5ff;
  padding: 10px 16px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.apply-grammar-btn {
  margin-top: 10px;
  margin-bottom: 0;
}

</style>
